import React, { useState, useEffect } from 'react';

const Calculator = () => {
  // State management
  const [droneProgram, setDroneProgram] = useState({
    models: {},
    baseCount: 1,
    baseCost: 40000
  });

  const [manualProgram, setManualProgram] = useState({
    useCases: {}
  });

  const [selectedIndustry, setSelectedIndustry] = useState('downstream');
  const [selectedUseCases, setSelectedUseCases] = useState(new Set());
  const [selectedDroneModels, setSelectedDroneModels] = useState(new Set());
  const [expandedBreakdowns, setExpandedBreakdowns] = useState({});

  // Data definitions
  const useCaseData = {
    tanksinspectionexternal: { name: 'Tanks Inspection External', breakdown: { personnel: 2, hours: 4, rate: 350, travel: 500, equipment: 150 }, potentialSavings: 100000 },
    pipelineinspection: { name: 'Pipeline Inspection', breakdown: { personnel: 2, hours: 8, rate: 250, travel: 500, equipment: 500 }, potentialSavings: 300000 },
    gasleakdetectionassets: { name: 'Gas Leak Detection Assets', breakdown: { personnel: 1, hours: 2, rate: 350, travel: 150, equipment: 150 }, potentialSavings: 400000 },
    count: { name: 'Object Count', breakdown: { personnel: 1, hours: 1, rate: 350, travel: 100, equipment: 100 }, potentialSavings: 15000 },
    thermalmonitoring: { name: 'Thermal Monitoring', breakdown: { personnel: 1, hours: 2, rate: 250, travel: 150, equipment: 150 }, potentialSavings: 150000 },
    gasleakdetectionpipes: { name: 'Gas Leak Detection Pipes', breakdown: { personnel: 2, hours: 2, rate: 350, travel: 500, equipment: 150 }, potentialSavings: 700000 },
    rooftopinspection: { name: 'Rooftop Inspection', breakdown: { personnel: 2, hours: 2, rate: 250, travel: 150, equipment: 150 }, potentialSavings: 500000 },
    distributionlineinspection: { name: 'Distribution Line Inspection', breakdown: { personnel: 2, hours: 4, rate: 250, travel: 500, equipment: 100 }, potentialSavings: 750000 },
    substationinspection: { name: 'Substation Inspection', breakdown: { personnel: 2, hours: 4, rate: 350, travel: 500, equipment: 500 }, potentialSavings: 750000 },
    flareinspection: { name: 'Flare Inspection', breakdown: { personnel: 2, hours: 2, rate: 350, travel: 1500, equipment: 500 }, potentialSavings: 100000 },
    volumemeasurement: { name: 'Volume Measurement', breakdown: { personnel: 1, hours: 2, rate: 350, travel: 800, equipment: 150 }, potentialSavings: 80000 },
    tanklevelmeasurement: { name: 'Tank Level Measurement', breakdown: { personnel: 1, hours: 1, rate: 250, travel: 100, equipment: 50 }, potentialSavings: 25000 },
    floatingrooftanksinsp: { name: 'Floating Roof Tank Inspection', breakdown: { personnel: 2, hours: 4, rate: 350, travel: 250, equipment: 500 }, potentialSavings: 600000 },
    prepoststorminsp: { name: 'Pre-Storm Inspection', breakdown: { personnel: 2, hours: 8, rate: 300, travel: 200, equipment: 300 }, potentialSavings: 150000 },
    buildinginspection: { name: 'Building Inspection', breakdown: { personnel: 2, hours: 8, rate: 250, travel: 150, equipment: 1500 }, potentialSavings: 75000 },
    turnaroundprogress: { name: 'Progress Monitoring', breakdown: { personnel: 1, hours: 4, rate: 350, travel: 0, equipment: 0 }, potentialSavings: 1000000 },
    emergencyresponse: { name: 'Emergency Response', breakdown: { personnel: 2, hours: 2, rate: 350, travel: 500, equipment: 500 }, potentialSavings: 900000 },
    securitypatrol: { name: 'Security Patrol', breakdown: { personnel: 1, hours: 8, rate: 50, travel: 0, equipment: 100 }, potentialSavings: 40000 },
    electricthermal: { name: 'Electrical Inspection', breakdown: { personnel: 2, hours: 4, rate: 350, travel: 500, equipment: 500 }, potentialSavings: 650000 },
    sitemappingsurvey: { name: 'Site Mapping', breakdown: { personnel: 1, hours: 4, rate: 350, travel: 300, equipment: 500 }, potentialSavings: 50000 },
    digitaltwincreation: { name: 'Digital Twin Creation', breakdown: { personnel: 1, hours: 16, rate: 500, travel: 500, equipment: 1000 }, potentialSavings: 100000 },
    pondinspection: { name: 'Pond Inspection', breakdown: { personnel: 1, hours: 4, rate: 350, travel: 0, equipment: 0 }, potentialSavings: 50000 },
    vegetationmanagement: { name: 'Vegetation Management', breakdown: { personnel: 1, hours: 8, rate: 350, travel: 0, equipment: 0 }, potentialSavings: 100000 },
    railcarcount: { name: 'Rail Car Count', breakdown: { personnel: 1, hours: 3, rate: 350, travel: 0, equipment: 0 }, potentialSavings: 20000 },
    solarpanelinspection: { name: 'Solar Panel Inspection', breakdown: { personnel: 1, hours: 40, rate: 350, travel: 0, equipment: 500 }, potentialSavings: 500000 }
  };

  const industryUseCases = {
    downstream: [
      'tanksinspectionexternal', 'pipelineinspection', 'gasleakdetectionassets', 'count',
      'gasleakdetectionpipes', 'flareinspection', 'volumemeasurement', 'tanklevelmeasurement', 'floatingrooftanksinsp',
      'prepoststorminsp', 'buildinginspection', 'turnaroundprogress', 'emergencyresponse', 'securitypatrol',
      'electricthermal', 'sitemappingsurvey', 'distributionlineinspection', 'substationinspection',
      'digitaltwincreation', 'pondinspection', 'vegetationmanagement', 'railcarcount'
    ],
    heavyIndustrial: [
      'rooftopinspection', 'count', 'electricthermal', 'sitemappingsurvey',
      'distributionlineinspection', 'substationinspection', 'digitaltwincreation', 'vegetationmanagement', 'solarpanelinspection'
    ],
    utilities: [
      'thermalmonitoring', 'distributionlineinspection', 'substationinspection',
      'electricthermal', 'sitemappingsurvey', 'solarpanelinspection', 'vegetationmanagement'
    ]
  };

  const droneModelData = {
    airMax: { name: 'Air Max', cost: 60000 },
    airMaxOGI: { name: 'Air Max OGI', cost: 96500 }
  };

  const specialNotes = {
    pipelineinspection: 'Identification of issues in the insulation and/or integrity of the pipeline. Monitoring and detection of temperature changes or issues, as well as corrosion under insulation.',
    tanksinspectionexternal: 'Inspection of storage tanks to assess their structural condition, temperature differential, and the state of their exterior, to identify any insulation issues, corrosion, and/or pump manifold leaks. Insight includes: report and issue breakdown',
    gasleakdetectionassets: 'Detection of gas emissions based on OGI, RGB, and thermal imagery',
    count: 'Count individual objects on a 2D map',
    thermalmonitoring: 'Monitoring temperature levels on various assets and detection of any temperature abnormalities',
    gasleakdetectionpipes: 'Detection of gas emissions along a pipeline based on OGI, RGB, and thermal imagery',
    rooftopinspection: 'Inspection of rooftops to assess their structural condition and the state of their exterior to identify any corrosion, damage, or other integrity issues. Insight includes: report and issue breakdown',
    distributionlineinspection: 'Complete scan to verify the integrity of the distribution lines components including overgrown vegetation and other issues',
    substationinspection: 'Assessment of transformers, circuit breakers, switch gear, relays and associated components using thermal and RGB imagery',
    flareinspection: 'Inspection of the combustion system, flare stack, and associated components',
    volumemeasurement: 'Analyze the volume of the stockpile, and see any changes it has undergone',
    tanklevelmeasurement: 'Thermal camera inspection captures temperature variations on the tank\'s surface to identify the liquid level inside, enabling accurate, non-contact measurement even in hard-to-reach or hazardous areas.',
    floatingrooftanksinsp: 'Inspection of a floating roof tank uses both OGI and RGB cameras to detect leaks and assess the roof\'s condition. The inspection focuses on seals, fittings, vents, and the roof deck to identify emissions, corrosion, or mechanical issues.',
    prepoststorminsp: 'Inspection involves inspecting assets such as tanks, roofs, and infrastructure to assess their condition before and after a weather event, identifying structural damage, flooding, debris, or other impacts to support maintenance and insurance documentation.',
    buildinginspection: 'Inspection of structures to assess their condition, identify damage, corrosion, cracks, or maintenance issues, and provide documentation for repair planning or asset management.',
    turnaroundprogress: 'Captures aerial images and video throughout a facility shutdown or maintenance turnaround to track progress, verify work completion, identify safety or operational issues, and provide documentation for reporting and planning.',
    emergencyresponse: 'Provides real-time aerial imagery and thermal data to assess the situation, identify hazards, locate people or assets, and support rapid decision-making and coordination during incidents.',
    securitypatrol: 'Involves automated flights around your facility or perimeter, day or night with thermal capabilities, to monitor for intrusions, suspicious activity, or safety hazards, providing real-time video and alerts to enhance security and response.',
    electricthermal: 'Inspection of critical plant equipment including heaters, cooling towers, finfans, processing units, and boilers. Uses thermal imaging to identify hotspots, electrical faults, and insulation weak points to prevent component failure and unplanned shutdowns.',
    sitemappingsurvey: 'Complete scan of a large area to create an up to date high-resolution orthomosaic used for survey grade measurements and P&ID generation.',
    digitaltwincreation: 'Develop an up-to-date high quality 3D model of piece of equipment or entire facility to provide precise measurements and visual context for engineering, planning, and safety purposes',
    pondinspection: 'Identify challenges to ensure pond functions optimally by monitoring water levels, algae buildup and blockages',
    vegetationmanagement: 'Scan large area for encroaching vegetation to ensure ground is clear of debris and overgrowth',
    railcarcount: 'Log rail car numbers at loading points for tracking purposes',
    solarpanelinspection: 'Inspection of solar panels to look for hotspots, failures and failures to ensure system is performing optimally and determine any preventative maintenance needs'
  };

  const intervalMultipliers = { daily: 365, weekly: 52, monthly: 12, quarterly: 4, biannually: 2, annually: 1 };

  const formatCurrency = (value, cents = false) => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0
    });
  };

  // Calculation functions
  const calculateManualProgram = () => {
    let totalCost = 0;
    let totalInspections = 0;
    let totalPotentialSavings = 0;
    const useCaseCosts = [];

    for (const id in manualProgram.useCases) {
      const uc = manualProgram.useCases[id];
      const b = uc.breakdown;
      const costPerInspection = (uc.isCustom && uc.manualCostPerInspection > 0)
        ? uc.manualCostPerInspection
        : (b.personnel * b.hours * b.rate) + b.travel + b.equipment;
      const annualInspections = uc.assets * (intervalMultipliers[uc.interval] || 0);
      const annualCost = annualInspections * costPerInspection;

      totalCost += annualCost;
      totalInspections += annualInspections;
      totalPotentialSavings += uc.potentialSavings;
      useCaseCosts.push({ id, name: uc.name, annualCost, costPerInspection });
    }

    return { totalCost, totalInspections, totalPotentialSavings, useCaseCosts };
  };

  const calculateDroneProgram = () => {
    let totalDroneHardwareCost = 0;
    for (const id in droneProgram.models) {
      const model = droneProgram.models[id];
      totalDroneHardwareCost += model.count * model.cost;
    }
    const baseCost = droneProgram.baseCount * droneProgram.baseCost;
    return { totalCost: totalDroneHardwareCost + baseCost };
  };

  const manualResults = calculateManualProgram();
  const droneResults = calculateDroneProgram();
  const operationalSavings = manualResults.totalCost - droneResults.totalCost;
  const totalSavings = operationalSavings + manualResults.totalPotentialSavings;
  const droneCostPerInspection = manualResults.totalInspections > 0 ? droneResults.totalCost / manualResults.totalInspections : 0;

  // Export to CSV function
  const exportToCsv = () => {
    const csvContent = [];

    csvContent.push(['Guidelines for the Calculator']);
    csvContent.push(['']);
    csvContent.push(['The model includes both direct OPEX savings and potential savings based on estimated annual failure prevention.']);
    csvContent.push(['The manual inspection cost figures are benchmark values derived from industry analyses. These may vary depending on site conditions, inspection frequency, and local labor rates.']);
    csvContent.push(['The ability to detect emissions or anomalies that would otherwise go unnoticed is considered a key benefit, as it helps prevent downtime. Identifying issues early allows for simpler, less costly fixes whereas undetected anomalies could escalate and require shutting down part of the plant for repairs.']);
    csvContent.push(['Detection frequency and accuracy assumptions are based on average performance data from active deployments and may differ per site depending on environmental or operational factors.']);
    csvContent.push(['Cost of downtime is not directly calculated but can be referenced as an additional benefit, particularly for sites where production interruptions have significant financial impact.']);
    csvContent.push(['Safety and environmental benefits, while not directly quantified, should be acknowledged as part of the broader operational value (e.g., reduced emissions, improved compliance).']);
    csvContent.push(['The calculator is intended as a decision-support tool, not a financial statement, results are indicative and should be reviewed in the context of actual operational data.']);
    csvContent.push(['When presenting results, its recommended to validate key assumptions (e.g., inspection frequency, labor rates, failure probabilities) with site-specific data for accuracy.']);
    csvContent.push([]);
    csvContent.push([]);

    csvContent.push(['Manual Program Details']);
    csvContent.push(['Use Case', 'Assets', 'Interval', 'Cost Per Inspection', 'Personnel', 'Hours', 'Rate', 'Travel Cost', 'Equipment Cost', 'Annual Cost', 'Potential Savings']);
    if (Object.keys(manualProgram.useCases).length > 0) {
      for (const id in manualProgram.useCases) {
        const uc = manualProgram.useCases[id];
        const b = uc.breakdown;
        const costPerInspection = (uc.isCustom && uc.manualCostPerInspection > 0)
          ? uc.manualCostPerInspection
          : (b.personnel * b.hours * b.rate) + b.travel + b.equipment;
        const annualCost = (uc.assets * (intervalMultipliers[uc.interval] || 0)) * costPerInspection;

        let useCaseName = uc.name;
        if (specialNotes[id]) {
          useCaseName += '*';
        }
        csvContent.push([useCaseName, uc.assets, uc.interval, formatCurrency(costPerInspection), b.personnel, b.hours, formatCurrency(b.rate), formatCurrency(b.travel), formatCurrency(b.equipment), formatCurrency(annualCost), formatCurrency(uc.potentialSavings)]);

        if (specialNotes[id]) {
          csvContent.push([`* ${specialNotes[id]}`]);
        }
      }
    } else {
      csvContent.push(['No use cases selected']);
    }
    csvContent.push([]);

    csvContent.push(['Drone Program Details']);
    csvContent.push(['Item', 'Quantity', 'Cost Per Item', 'Total Cost']);
    for (const id in droneProgram.models) {
      const model = droneProgram.models[id];
      const modelData = droneModelData[id];
      csvContent.push([modelData.name, model.count, formatCurrency(model.cost), formatCurrency(model.count * model.cost)]);
    }
    csvContent.push(['Bases', droneProgram.baseCount, formatCurrency(droneProgram.baseCost), formatCurrency(droneProgram.baseCount * droneProgram.baseCost)]);
    csvContent.push([]);

    const getStatus = (value) => value >= 10000 ? 'Positive' : (value <= -10000 ? 'Negative' : 'Neutral');
    csvContent.push(['ROI Verdict']);
    csvContent.push(['Metric', 'Value', 'Status']);
    csvContent.push(['B. Total Manual Program Cost', formatCurrency(manualResults.totalCost), '']);
    csvContent.push(['A. Total Drone Program Cost', formatCurrency(droneResults.totalCost), '']);
    csvContent.push(['Operational Savings (B - A)', formatCurrency(operationalSavings), getStatus(operationalSavings)]);
    csvContent.push(['Potential Additional Savings', formatCurrency(manualResults.totalPotentialSavings), '']);
    csvContent.push(['Total Estimated Annual Value', formatCurrency(totalSavings), getStatus(totalSavings)]);

    const csvString = csvContent.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "roi_analysis.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Industry change handler
  useEffect(() => {
    setSelectedUseCases(new Set());
    setManualProgram({ useCases: {} });
  }, [selectedIndustry]);

  // Update manual program when use cases are selected/deselected
  useEffect(() => {
    const newUseCases = {};
    selectedUseCases.forEach(id => {
      if (manualProgram.useCases[id]) {
        newUseCases[id] = manualProgram.useCases[id];
      } else {
        const uc = useCaseData[id];
        newUseCases[id] = {
          name: uc.name,
          assets: 1,
          interval: 'monthly',
          manualCostPerInspection: 0,
          potentialSavings: uc.potentialSavings,
          breakdown: { ...uc.breakdown },
          isCustom: false
        };
      }
    });
    setManualProgram({ useCases: newUseCases });
  }, [selectedUseCases]);

  // Update drone program when models are selected/deselected
  useEffect(() => {
    const newModels = {};
    selectedDroneModels.forEach(id => {
      if (droneProgram.models[id]) {
        newModels[id] = droneProgram.models[id];
      } else {
        newModels[id] = {
          count: 1,
          cost: droneModelData[id].cost
        };
      }
    });
    setDroneProgram(prev => ({ ...prev, models: newModels }));
  }, [selectedDroneModels]);

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">ROI Calculator</h1>
          <p className="text-gray-600 mt-2 max-w-3xl mx-auto">
            Compare the long-term value of a scalable drone program against traditional manual inspections across different use cases.
          </p>
        </div>

        {/* Master Input Panel */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <h2 className="text-2xl font-bold text-gray-800 border-b pb-3 mb-6">Master Input Panel</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-12">

            {/* Solution A: Drone Program */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-700">Solution A: Scalable Drone Program</h3>
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Select Drone Models</label>
                  <div className="mt-1 space-y-2 rounded-md border border-gray-200 bg-white p-2">
                    {Object.entries(droneModelData).map(([id, model]) => (
                      <div key={id} className="p-2 hover:bg-gray-100 cursor-pointer">
                        <label className="flex items-center space-x-3 w-full cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDroneModels.has(id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedDroneModels);
                              if (e.target.checked) {
                                newSet.add(id);
                              } else {
                                newSet.delete(id);
                              }
                              setSelectedDroneModels(newSet);
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{model.name}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {Array.from(selectedDroneModels).map(id => {
                    const model = droneProgram.models[id];
                    if (!model) return null;
                    return (
                      <div key={id} className="p-4 border border-gray-200 rounded-lg bg-white">
                        <h5 className="font-bold text-gray-800">{droneModelData[id].name}</h5>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Number of Drones</label>
                            <input
                              type="number"
                              value={model.count}
                              onChange={(e) => {
                                const newModels = { ...droneProgram.models };
                                newModels[id] = { ...newModels[id], count: parseFloat(e.target.value) || 0 };
                                setDroneProgram({ ...droneProgram, models: newModels });
                              }}
                              min="0"
                              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600">Cost per Drone ($)</label>
                            <input
                              type="number"
                              value={model.cost}
                              onChange={(e) => {
                                const newModels = { ...droneProgram.models };
                                newModels[id] = { ...newModels[id], cost: parseFloat(e.target.value) || 0 };
                                setDroneProgram({ ...droneProgram, models: newModels });
                              }}
                              min="0"
                              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-semibold text-gray-700">Shared Program Costs</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Number of Bases</label>
                    <input
                      type="number"
                      value={droneProgram.baseCount}
                      onChange={(e) => setDroneProgram({ ...droneProgram, baseCount: parseFloat(e.target.value) || 0 })}
                      min="0"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Cost per Base ($)</label>
                    <input
                      type="number"
                      value={droneProgram.baseCost}
                      onChange={(e) => setDroneProgram({ ...droneProgram, baseCost: parseFloat(e.target.value) || 0 })}
                      min="0"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Solution B: Manual Program */}
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-700">Solution B: Traditional Manual Program</h3>
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600">Industry</label>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="downstream">Downstream</option>
                    <option value="heavyIndustrial">Heavy Industrial</option>
                    <option value="utilities">Utilities</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600">Select Use Cases</label>
                  <div className="mt-1 h-60 border border-gray-200 rounded-md bg-white overflow-y-auto">
                    {industryUseCases[selectedIndustry].map(id => (
                      <div key={id} className="p-2 hover:bg-gray-100 cursor-pointer">
                        <label className="flex items-center space-x-3 w-full cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUseCases.has(id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedUseCases);
                              if (e.target.checked) {
                                newSet.add(id);
                              } else {
                                newSet.delete(id);
                              }
                              setSelectedUseCases(newSet);
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{useCaseData[id].name}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="text-right mt-2">
                    <button
                      onClick={() => {
                        const customId = `custom-${Date.now()}`;
                        const newSet = new Set(selectedUseCases);
                        newSet.add(customId);
                        setSelectedUseCases(newSet);
                        setManualProgram({
                          ...manualProgram,
                          useCases: {
                            ...manualProgram.useCases,
                            [customId]: {
                              name: 'Custom Use Case',
                              assets: 0,
                              interval: 'monthly',
                              manualCostPerInspection: 0,
                              potentialSavings: 0,
                              breakdown: { personnel: 0, hours: 0, rate: 0, travel: 0, equipment: 0 },
                              isCustom: true
                            }
                          }
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
                    >
                      + Add Custom Use Case
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Manual Use Case Costs */}
        {Object.keys(manualProgram.useCases).length > 0 && (
          <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
            <h2 className="text-2xl font-bold text-gray-800 border-b pb-3 mb-6">Manual Use Case Costs</h2>

            <div className="hidden sm:grid grid-cols-12 gap-4 px-2 pb-2 text-xs font-semibold text-gray-500">
              <div className="col-span-3">Use Case</div>
              <div className="col-span-2 text-center">Cost Per Insp.</div>
              <div className="col-span-1 text-center">Assets</div>
              <div className="col-span-2 text-center">Interval</div>
              <div className="col-span-2 text-center">Potential Savings</div>
              <div className="col-span-2 text-right">Total Annual Cost</div>
            </div>

            <div className="divide-y divide-gray-200">
              {Object.entries(manualProgram.useCases).map(([id, uc]) => {
                const b = uc.breakdown;
                const costPerInspection = (uc.isCustom && uc.manualCostPerInspection > 0)
                  ? uc.manualCostPerInspection
                  : (b.personnel * b.hours * b.rate) + b.travel + b.equipment;
                const annualCost = (uc.assets * (intervalMultipliers[uc.interval] || 0)) * costPerInspection;

                return (
                  <div key={id} className="p-2">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                      <div className="sm:col-span-3">
                        {uc.isCustom ? (
                          <input
                            type="text"
                            value={uc.name}
                            onChange={(e) => {
                              const newUseCases = { ...manualProgram.useCases };
                              newUseCases[id] = { ...newUseCases[id], name: e.target.value };
                              setManualProgram({ ...manualProgram, useCases: newUseCases });
                            }}
                            placeholder="Enter Use Case Name"
                            className="w-full px-2 py-1 text-sm font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <h5 className="font-semibold text-gray-800 text-sm">
                            {uc.name}{specialNotes[id] ? '*' : ''}
                          </h5>
                        )}
                        <div className="mt-1 space-x-2">
                          <button
                            onClick={() => setExpandedBreakdowns(prev => ({ ...prev, [id]: !prev[id] }))}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {expandedBreakdowns[id] ? 'Hide' : 'Edit'} Breakdown
                          </button>
                          {uc.isCustom && (
                            <button
                              onClick={() => {
                                const newSet = new Set(selectedUseCases);
                                newSet.delete(id);
                                setSelectedUseCases(newSet);
                              }}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="sm:hidden text-xs font-medium text-gray-500">Cost Per Inspection:</label>
                        {uc.isCustom ? (
                          <input
                            type="number"
                            value={uc.manualCostPerInspection}
                            onChange={(e) => {
                              const newUseCases = { ...manualProgram.useCases };
                              newUseCases[id] = { ...newUseCases[id], manualCostPerInspection: parseFloat(e.target.value) || 0 };
                              setManualProgram({ ...manualProgram, useCases: newUseCases });
                            }}
                            min="0"
                            placeholder="Override"
                            className="w-full px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <div className="text-center font-medium text-gray-800">{formatCurrency(costPerInspection)}</div>
                        )}
                      </div>

                      <div className="sm:col-span-1">
                        <label className="sm:hidden text-xs font-medium text-gray-500">Assets:</label>
                        <input
                          type="number"
                          value={uc.assets}
                          onChange={(e) => {
                            const newUseCases = { ...manualProgram.useCases };
                            newUseCases[id] = { ...newUseCases[id], assets: parseFloat(e.target.value) || 0 };
                            setManualProgram({ ...manualProgram, useCases: newUseCases });
                          }}
                          min="0"
                          className="w-full px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="sm:hidden text-xs font-medium text-gray-500">Interval:</label>
                        <select
                          value={uc.interval}
                          onChange={(e) => {
                            const newUseCases = { ...manualProgram.useCases };
                            newUseCases[id] = { ...newUseCases[id], interval: e.target.value };
                            setManualProgram({ ...manualProgram, useCases: newUseCases });
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="biannually">Bi-Annually</option>
                          <option value="annually">Annually</option>
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="sm:hidden text-xs font-medium text-gray-500">Potential Savings:</label>
                        <input
                          type="number"
                          value={uc.potentialSavings}
                          onChange={(e) => {
                            const newUseCases = { ...manualProgram.useCases };
                            newUseCases[id] = { ...newUseCases[id], potentialSavings: parseFloat(e.target.value) || 0 };
                            setManualProgram({ ...manualProgram, useCases: newUseCases });
                          }}
                          min="0"
                          className="w-full px-2 py-1 text-sm text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="sm:col-span-2 text-right font-bold text-gray-900">
                        <label className="sm:hidden text-xs font-medium text-gray-500">Total Annual Cost: </label>
                        {formatCurrency(annualCost)}
                      </div>
                    </div>

                    {expandedBreakdowns[id] && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                          {Object.entries(b).map(([key, value]) => (
                            <div key={key}>
                              <label className="block text-xs font-medium text-gray-500">
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </label>
                              <input
                                type="number"
                                value={value}
                                onChange={(e) => {
                                  const newUseCases = { ...manualProgram.useCases };
                                  newUseCases[id].breakdown[key] = parseFloat(e.target.value) || 0;
                                  setManualProgram({ ...manualProgram, useCases: newUseCases });
                                }}
                                min="0"
                                step={key === 'hours' ? '0.5' : '1'}
                                className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {specialNotes[id] && (
                      <p className="text-xs text-gray-500 mt-2 pl-1">* {specialNotes[id]}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cost Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md text-center">
            <h2 className="text-xl font-bold text-gray-700">Solution A: Scalable Drone Program</h2>
            <div className="mt-6">
              <p className="text-sm text-gray-500 uppercase">Total Annual Cost</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(droneResults.totalCost)}</p>
            </div>
            <div className="mt-6">
              <p className="text-sm text-gray-500 uppercase">Cost Per Inspection (Blended)</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(droneCostPerInspection, true)}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-bold text-gray-700 text-center">Solution B: Traditional Manual Program</h2>
            <div className="mt-4 border-t pt-4">
              <ul className="space-y-2 text-sm">
                {manualResults.useCaseCosts.length > 0 ? (
                  manualResults.useCaseCosts.map(uc => (
                    <li key={uc.id} className="flex justify-between">
                      <span>{uc.name}:</span>
                      <span className="font-medium">{formatCurrency(uc.annualCost)}</span>
                    </li>
                  ))
                ) : (
                  <li className="text-center text-gray-500">Select or add use cases for a cost breakdown.</li>
                )}
              </ul>
            </div>
            <div className="mt-4 border-t pt-4 text-center">
              <p className="text-sm text-gray-500 uppercase">Total Annual Cost</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(manualResults.totalCost)}</p>
            </div>
          </div>
        </div>

        {/* ROI Verdict */}
        <div className="bg-gray-800 text-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-center">ROI Verdict</h2>
          <div className="mt-6 max-w-2xl mx-auto space-y-4 text-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">B. Total Manual Program Cost</span>
              <span className="font-semibold text-white">{formatCurrency(manualResults.totalCost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">A. Total Drone Program Cost</span>
              <span className="font-semibold text-white">{formatCurrency(droneResults.totalCost)}</span>
            </div>
            <div className="flex justify-between items-center border-t border-gray-600 pt-4">
              <span className="text-gray-400">Operational Savings (B - A)</span>
              <span className="font-semibold text-xl text-white">{formatCurrency(operationalSavings)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Potential Additional Savings</span>
              <span className="font-semibold text-xl text-blue-400">{formatCurrency(manualResults.totalPotentialSavings)}</span>
            </div>
          </div>

          <div className="mt-8 text-center border-t border-gray-600 pt-6">
            <p className="text-lg text-gray-300">Total Estimated Annual Value:</p>
            <p className={`text-5xl font-bold mt-2 ${
              totalSavings >= 10000 ? 'text-green-500' :
              totalSavings <= -10000 ? 'text-red-500' :
              'text-orange-500'
            }`}>
              {formatCurrency(totalSavings)}
            </p>
            <button
              onClick={exportToCsv}
              className="mt-6 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Export to CSV
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-600 max-w-4xl mx-auto">
            <h3 className="text-lg font-semibold text-center text-gray-200 mb-4">Guidelines for the Calculator</h3>
            <ul className="space-y-2 text-sm text-gray-400 list-disc list-inside">
              <li>The model includes both direct OPEX savings and potential savings based on estimated annual failure prevention.</li>
              <li>The manual inspection cost figures are benchmark values derived from industry analyses. These may vary depending on site conditions, inspection frequency, and local labor rates.</li>
              <li>The ability to detect emissions or anomalies that would otherwise go unnoticed is considered a key benefit, as it helps prevent downtime. Identifying issues early allows for simpler, less costly fixes whereas undetected anomalies could escalate and require shutting down part of the plant for repairs.</li>
              <li>Detection frequency and accuracy assumptions are based on average performance data from active deployments and may differ per site depending on environmental or operational factors.</li>
              <li>Cost of downtime is not directly calculated but can be referenced as an additional benefit, particularly for sites where production interruptions have significant financial impact.</li>
              <li>Safety and environmental benefits, while not directly quantified, should be acknowledged as part of the broader operational value (e.g., reduced emissions, improved compliance).</li>
              <li>The calculator is intended as a decision-support tool, not a financial statement, results are indicative and should be reviewed in the context of actual operational data.</li>
              <li>When presenting results, its recommended to validate key assumptions (e.g., inspection frequency, labor rates, failure probabilities) with site-specific data for accuracy.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;